import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { sendNotification } from '@/lib/notifications/send-notification';

export type PendingTutorInvitation = {
  link_id: string;
  tutor_id: string;
  tutor_name: string;
  tutor_avatar_url: string | null;
  /** Matière(s) proposées par le tuteur (`tutor_links.subjects`) — vide = toutes matières. */
  subjects: SubjectSlug[];
  invited_at: string;
};

export function usePendingTutorInvitations(studentId: string) {
  return useQuery({
    queryKey: ['pending-tutor-invitations', studentId],
    enabled: !!studentId,
    queryFn: async (): Promise<PendingTutorInvitation[]> => {
      type Row = {
        id: string;
        tutor_id: string;
        subjects: SubjectSlug[];
        invited_at: string;
        profiles: { display_name: string | null; avatar_url: string | null } | null;
      };

      const result = await supabase
        .from('tutor_links')
        .select(
          'id, tutor_id, subjects, invited_at, profiles!tutor_links_tutor_id_fkey(display_name:visible_name, avatar_url)',
        )
        .eq('student_id', studentId)
        .eq('status', 'pending')
        // L'élève ne doit voir que les invitations émises par le prof.
        // Les demandes qu'il a lui-même envoyées attendent la validation du prof.
        .eq('initiated_by', 'tutor')
        .order('invited_at', { ascending: true });

      if (result.error) throw new Error(result.error.message);

      // supabase-js 2.104+ ne résout pas le type de la FK ambiguë à la compilation
      const rows = (result.data ?? []) as unknown as Row[];
      return rows.map((row) => ({
        link_id: row.id,
        tutor_id: row.tutor_id,
        tutor_name: row.profiles?.display_name ?? 'Professeur',
        tutor_avatar_url: row.profiles?.avatar_url ?? null,
        subjects: row.subjects ?? [],
        invited_at: row.invited_at,
      }));
    },
  });
}

export function useRespondTutorInvitation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ linkId, accept }: { linkId: string; accept: boolean }) => {
      const { error } = await supabase
        .from('tutor_links')
        .update({
          status: accept ? 'active' : 'revoked',
          ...(accept ? { accepted_at: new Date().toISOString() } : {}),
        })
        .eq('id', linkId)
        .eq('student_id', studentId);
      if (error) throw new Error(error.message);
      return { linkId, accept };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pending-tutor-invitations', studentId] });
      qc.invalidateQueries({ queryKey: ['tutor-assignments', studentId] });
      if (data.accept) {
        sendNotification({ type: 'tutor_invite_accepted', link_id: data.linkId });
      }
    },
  });
}
